import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  getCountries,
  getCountryCallingCode,
} from 'react-phone-number-input/input';

// TODO Correct translation for dutch based on portal https://github.com/catamphetamine/react-phone-number-input/tree/master/locale
import de from 'react-phone-number-input/locale/de.json';
import el from 'react-phone-number-input/locale/el.json';
import en from 'react-phone-number-input/locale/en.json';
import es from 'react-phone-number-input/locale/es.json';
import fi from 'react-phone-number-input/locale/fi.json';
import fr from 'react-phone-number-input/locale/fr.json';
import it from 'react-phone-number-input/locale/it.json';
import nb from 'react-phone-number-input/locale/nb.json';
import pl from 'react-phone-number-input/locale/pl.json';
import pt from 'react-phone-number-input/locale/pt.json';
import ru from 'react-phone-number-input/locale/ru.json';
import sv from 'react-phone-number-input/locale/sv.json';
import vi from 'react-phone-number-input/locale/vi.json';

// Components
import CountrySelectPopout from './CountrySelectPopout';

export function getSelectedOption(options, value) {
  for (const option of options) {
    if (!option.divider && option.value === value) {
      return option;
    }
  }
}

const localeToTranslationMap = {
  de,
  el,
  en,
  es,
  fi,
  fr,
  it,
  nb,
  pl,
  pt,
  ru,
  sv,
  vi,
};

const generateOption = (locale, value) => {
  let translatedLabel = localeToTranslationMap[locale] || en;
  return {
    key: value,
    value: value,
    label: `${translatedLabel[value]} +${getCountryCallingCode(value)}`,
  };
};

const order = {
  NL: 0,
  GB: 1,
  FR: 2,
  DE: 3,
  ES: 4,
  IT: 5,
  BE: 6,
  DK: 7,
  PL: 8,
};

function mapOrder(countries, locale) {
  let orderedCountries = countries.reduce((acc, current) => {
    let item = generateOption(locale, current);

    if (order[current] <= 8) {
      let index = order[current];
      acc[index] = item;
    } else if (acc.length >= 9) {
      acc[acc.length] = item;
    } else {
      acc[9] = item;
    }

    return acc;
  }, []);

  return orderedCountries;
}

const CountrySelectWithIcon = ({
  value,
  onChange,
  className,
  iconComponent,
  locale,
  ...rest
}) => {
  let options = locale ? mapOrder(getCountries(), locale) : rest.options;

  const selectedOption = useMemo(() => {
    return getSelectedOption(options, value);
  }, [options, value]);
  // tslint:disable-next-line:variable-name
  const onChange_ = useCallback(
    valueObject => {
      // tslint:disable-next-line:no-shadowed-variable
      const value = valueObject.value;
      onChange(value === 'ZZ' ? undefined : value);
    },
    [onChange]
  );

  return (
    <CountrySelectPopout
      onChange={onChange_}
      options={options}
      value={value}
      className={className}
      iconComponent={iconComponent}
      selectedOption={selectedOption}
      countryFlagClassname={'mr-10'}
    />
  );
};

CountrySelectWithIcon.defaultProps = {
  options: mapOrder(getCountries()),
};

CountrySelectWithIcon.propTypes = {
  className: PropTypes.string,
  iconComponent: PropTypes.elementType, // Country flag component.
  locale: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      divider: PropTypes.bool,
    })
  ),
  unicodeFlags: PropTypes.bool, // Set to `true` to render Unicode flag icons instead of SVG images.
  value: PropTypes.string, // A two-letter country code. Example: "US", "RU". "ZZ" means "International".
};

export default CountrySelectWithIcon;
